import React from "react";
import addDays from "date-fns/addDays";
import axios from "axios";
import moment from 'moment';
import { AlertColor, Button, Link, TextField, useMediaQuery} from "@mui/material";
import { Add } from "@mui/icons-material";
import { DataGridPro, GridCellParams, GridColumns, GridSelectionModel, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../../component/Title";
import { BaseControl, DatePickerControl, DropDownComboControl, GenericFilter } from "../../../component/GenericFilter";
import { globalTheme } from "../../../../common/theme";
import { useConstructor } from "../../../../common/UseConstructor";
import { StatementFilter } from "../../../../models/classes/StatementFilter";
import { CustomTablePagination } from "../../../component/CustomPagination";
import { useAppSelector } from "../../../../common/hooks";
import { RootState } from "../../../../common/Store";
import { CommonProps } from "../../../component/MainContents";
import { BranchInfo } from "../../branches/BranchAccessories";
import { Pad } from "../../../../common/Formatter";
import { getDefaultRegion, renewToken } from "../../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../../component/SnackBarMessage";
import { useNavigate } from "react-router-dom";
import { OpenMode } from "../../../../common/Enums";
import { Statement } from "./StatementAccessories";
import { StatementSingle, StatementSingleHandle } from "./StatementSingle";
import { MonthNames } from "../../../../common/Constants";

export function StatementList(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<StatementSingleHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');
      
    const [controls, setControls] = React.useState<BaseControl[]>([]); //For filtering

    const [searchData, setSearchData] = React.useState<any>({
        contains: true,
        searchText: "",
        customer: 0,
        from: addDays(new Date(), -60),
        to: new Date(),
        pageSize: 15,
    });

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function LoadData() {
		setLoading(true);
        let filter = new StatementFilter({
			From: moment(searchData.from).format(),
			To: moment(searchData.to).format(),
			CustomerID: searchData.customer % 100000,
			StatementNo: searchData.searchText
		});

		axios.post<Statement[]>('Api/V1/Statement/Search', filter, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				result.data.forEach(i => {
					i.id = i.statementNo;
				});

				setGridData(result.data.sort((a, b) => {
					if (a.statementDate > b.statementDate) { return -1; }
					if (a.statementDate < b.statementDate) { return 1; }
					if (a.statementDate === b.statementDate)
					{
						if (a.statementNo > b.statementNo) { return -1; }
						if (a.statementNo < b.statementNo) { return 1; }
					}
					return 0;					
				}));
				setLoading(false);
			},
				(error) => {
					setGridData([]);
					setLoading(false);
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		setControls([
			{
				key: 'from',
				title: 'From',
				dateValue: addDays(new Date(), -7)
			} as DatePickerControl,
			{
				key: 'to',
				title: 'To',
				dateValue: new Date()
			} as DatePickerControl,
			{
				key: 'customer',
				title: 'Customer',
				dropdownOptions: props.customers
			} as DropDownComboControl
		]);
		LoadData();
    }

	//Grid columns
	const columns: GridColumns = [
		{
			field: 'statementNo', headerName: 'Statement No', flex: 1, renderCell: (params: GridCellParams) => (
				<Link
					href="#"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						EditObject(params.id.toString(), params.row.customerID);
					}}
					color="inherit"
					underline="hover">
					<strong>
						{params.value}
					</strong>
				</Link>
			)
		},
		{ field: 'customerName', headerName: 'Customer', flex: 4, type: 'string' },
		{ field: 'statementDate', headerName: 'Date', flex: 1, 
			renderCell: (params: GridCellParams) => (
				<TextField variant="standard" value={moment(params.value).format('YYYY/MM/DD')} InputProps={{ disableUnderline: true }}/>
			)
		},
	];

	const [gridData, setGridData] = React.useState<Statement[]>([]);
	const selectedData = React.useRef<Statement[]>([]);

	function CustomToolbar() {
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Add />} onClick={() => NewObject()}>New</Button>
			</GridToolbarContainer>
		);
	}

	function NewObject() {
		axios.get<BranchInfo[]>(`Api/V1/Branch`, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				let defaultBranch: BranchInfo | undefined = result.data.find(b => b.region === getDefaultRegion());
				if (defaultBranch)
				{
					let statement: Statement = {
						id: "",
						statementNo: "ME" + defaultBranch.region + (new Date().getFullYear()).toString().substring(2, 4) + MonthNames[defaultBranch.statementMonth - 1].toUpperCase().substring(0, 3) + Pad(defaultBranch.statementNo, 4),
						custLangID: 1,
						customerID: 0,
						customerName: "",
						statementDate: moment(new Date()).format(),
						staffID: 0,
						staff: "",
						attention: "",
						bankPhone: "",
						paymentTerm: "30 days after delivery",
						remark: "",
						statementRecords: [],
						bankName: "",
						bankAddress: "",
						bankAccount: "",
						cc: "",
						title: "",
						phone1: "",
						phone2: "",
						fax1: "",
						fax2: "",
						address: ""
					}
					singleModalRef.current?.Initialise(OpenMode.NEW, statement);
				}
			},
				(error) => {
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	function EditObject(statementNo: string, customerId: number) {
        setLoading(true);

		var lastMonthFirstDdate = new Date();
        lastMonthFirstDdate.setDate(1);
        lastMonthFirstDdate.setMonth(lastMonthFirstDdate.getMonth()-1);

        var thisMonthFirstDdate = new Date();
        thisMonthFirstDdate.setDate(1);

		let filter = new StatementFilter({
			From: moment(lastMonthFirstDdate).format(),
			To: moment(thisMonthFirstDdate).format(),
			CustomerID: customerId,
			StatementNo: statementNo
		});

		axios.post<Statement>('Api/V1/Statement/Get', filter, {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				result.data.statementRecords.forEach(i => {
					i.id = i.recordID;
				});
				singleModalRef.current?.Initialise(OpenMode.EDIT, result.data);
				setLoading(false);
			},
			  (error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
			)
			.catch((error) => {
			  setLoading(false);
			  console.log(error);
			  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

    function DoSearch(event: React.FormEvent<any>) {
		if (event)
        	event.preventDefault();

		LoadData();
    }

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Statement List" />
			<GenericFilter controls={controls} searchData={searchData} setSearchData={setSearchData} doSearch={DoSearch} searchBoxLabel="Statement No" />
			<div className={style.PageContent}>
				<DataGridPro
					rows={gridData}
					columns={columns}
					pageSize={searchData.pageSize}
					onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
					pagination
					rowsPerPageOptions={[15, 30, 50, 100]}
					loading={loading}
					components={{
						Toolbar: CustomToolbar,
						Footer: CustomTablePagination
					}}
					componentsProps={{
						footer: { rowsPerPageOptions: [15, 30, 50, 100] }
					}}
					headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
					rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
					onRowDoubleClick={(params) => EditObject(params.id.toString(), params.row.customerID)}
				/>
			</div>

			<StatementSingle
				ref={singleModalRef}
				customers={props.customers}
				regions={props.regions}
				paymentTerms={props.paymentTerms}
				endUsers={[]} 
				sales={[]} 
				deliveryMethods={[]} 
				currencies={props.currencies}
				onClose={(saved: boolean, statement: Statement) => {
					LoadData();
				} } />

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

